import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';

import {
  GET_MEDIA,
  MEDIA_BULK_DELETE,
  MEDIA_DELETE,
  MEDIA_UPDATE,
} from '@atom/graph/media';
import { useFileDownload } from '@atom/hooks/useFileDownload';
import { useFileUpload } from '@atom/hooks/useFileUpload';
import { Media, MediaType, SubjectType } from '@atom/types/media';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_CHARACTER_MIN = 3;
const UPLOAD_WIDTHS = [180, 1440];

type UseAssetPhotosOptions = {
  assetId: string;
  page: number;
  limit: number;
  search: string;
};

const useAssetPhotos = ({
  assetId,
  page,
  limit,
  search,
}: UseAssetPhotosOptions) => {
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((next: string) => setDebouncedSearch(next), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  const isSearching = debouncedSearch.length >= SEARCH_CHARACTER_MIN;

  const mediaVariables = {
    input: {
      parentSubjectIds: [assetId],
      subjectTypes: [SubjectType.INVENTORY_ASSET],
      type: MediaType.IMAGE,
      page,
      limit,
      ...(isSearching && { name: debouncedSearch }),
    },
  };

  const { data, previousData, loading, refetch } = useQuery(GET_MEDIA, {
    variables: mediaVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const refetchMediaQuery = [{ query: GET_MEDIA, variables: mediaVariables }];

  const [mediaUpdate] = useMutation(MEDIA_UPDATE, {
    refetchQueries: refetchMediaQuery,
    awaitRefetchQueries: true,
  });

  const [mediaDelete] = useMutation(MEDIA_DELETE, {
    refetchQueries: refetchMediaQuery,
    awaitRefetchQueries: true,
  });

  const [mediaBulkDelete] = useMutation(MEDIA_BULK_DELETE, {
    refetchQueries: refetchMediaQuery,
    awaitRefetchQueries: true,
  });

  const [uploadFiles] = useFileUpload();
  const [downloadFiles] = useFileDownload();

  const uploadPhotos = async (files: FileList) => {
    await uploadFiles({
      files,
      subjectId: assetId,
      subjectType: SubjectType.INVENTORY_ASSET,
      parentSubjectId: assetId,
      widths: UPLOAD_WIDTHS,
    });

    await refetch();
  };

  const renamePhoto = async (id: string, name: string) => {
    await mediaUpdate({ variables: { input: { id, name } } });
  };

  const deletePhoto = async (id: string) => {
    await mediaDelete({ variables: { id } });
  };

  const deletePhotos = async (ids: string[]) => {
    await mediaBulkDelete({ variables: { input: { ids } } });
  };

  const downloadPhotos = async (photos: Media[]) => {
    await downloadFiles(photos);
  };

  const refetchPhotos = async () => {
    await refetch();
  };

  const connection = data?.media || previousData?.media;

  return {
    media: connection?.media || [],
    totalCount: connection?.totalCount || 0,
    mediaLoading: loading || search !== debouncedSearch,
    uploadPhotos,
    renamePhoto,
    deletePhoto,
    deletePhotos,
    downloadPhotos,
    refetchPhotos,
  };
};

export default useAssetPhotos;
